import {track} from "@amplitude/analytics-browser";
import {faMap} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Carousel} from "@mantine/carousel";
import {
  Anchor,
  Breadcrumbs,
  Button,
  Card,
  Center,
  Container,
  Divider,
  Grid,
  Group,
  Image,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import className from "classnames";
import dayjs from "dayjs";
import parse from "html-react-parser";
import {isEmpty} from "lodash";
import type {GetServerSideProps} from "next";
import Head from "next/head";
import Link from "next/link";
import pluralize from "pluralize";
import React, {useEffect, useMemo} from "react";
import NoSSR from "react-no-ssr";

import classes from "./FacilityPage.module.scss";
import ReserveFacilityButton from "../../components/buttons/ReserveFacilityButton";
import WatchButton from "../../components/buttons/WatchButton";
import FacilityUnitsTable from "../../components/FacilityUnitsTable/FacilityUnitsTable";
import Footer from "../../components/Footer";
import InfoCard from "../../components/InfoCard";
import MediaCarousel from "../../components/MediaCarousel";
import Rating from "../../components/Rating";
import Reviews from "../../components/Reviews";
import {areFiltersApplied} from "../../components/Search/FilterPanel/FilterPanel";
import Spoiler from "../../components/Spoiler";
import {
  APP_DESCRIPTION,
  APP_NAME,
  DEFAULT_SLOTS_DAYS,
  NOT_FOUND_IMAGE_URL,
} from "../../constants";
import {default as apolloClient} from "../../graphql/client";
import * as queries from "../../graphql/queries";
import type {Address, Facility, MapSearchInput} from "../../graphql/types";
import {
  queryStringToSearchState,
  searchStateToQueryString,
} from "../../lib/search";
import * as utils from "../../lib/utils";
import {getMapUrl, suffixWithAppName} from "../../lib/utils";

export interface FacilityPageProps {
  facility: Partial<Facility>; // Facility to display
  searchInput?: MapSearchInput; // User search input (used for filtering units)
  withFilterEnabled?: boolean;
}

/**
 * Facility details page
 */
export default function FacilityPage({
  facility,
  searchInput,
  withFilterEnabled = true,
}: FacilityPageProps) {
  useEffect(() => {
    track("Facility Page Viewed", {
      facility: {
        id: facility.id,
        slug: facility.slug,
        name: facility.name,
        searchInput,
      },
    });
  }, []);

  let description = facility.descriptionHtml
    ? parse(facility.descriptionHtml || "")
    : facility.description;

  if (description === facility.name) {
    description = null;
  }

  const title = facility.name ? facility.name : `Facility #${facility.id}`;
  const seoDescription = utils.formatSEODescription(
    facility.description ||
      facility.park?.description ||
      facility.name ||
      facility.park?.name ||
      APP_DESCRIPTION
  );

  const searchMapUrl = useMemo(() => {
    if (!facility?.location) {
      return null;
    }

    const queryString = searchStateToQueryString({
      searchInput: {
        ...searchInput,
        zoom: 13,
      },
      mapCenter: facility.location,
    });

    return `/search?${queryString}`;
  }, [searchInput, facility]);

  const address: Address | undefined = useMemo(() => {
    const addresses = facility.addresses || [];
    if (addresses.length === 0) {
      return undefined;
    }

    if (addresses.length === 1) {
      return addresses[0];
    }

    const defaultAddrs = addresses.filter(
      (address: Address) => address.type === "default"
    );
    if (defaultAddrs.length === 0) {
      return addresses[0];
    }

    return defaultAddrs[0];
  }, [facility]);

  return (
    <>
      <Head>
        <title key="title">{suffixWithAppName(title)}</title>

        <meta name="description" content={seoDescription} />

        <meta property="og:title" content={facility.name || APP_NAME} />
        <meta property="og:description" content={seoDescription} />
        {facility.primaryMedia?.url && (
          <>
            <meta property="og:image" content={facility.primaryMedia.url} />
            {/^https:/.test(facility.primaryMedia.url) && (
              <meta
                property="og:image:secure_url"
                content={facility.primaryMedia.url}
              />
            )}
            {facility.primaryMedia.title && (
              <meta
                property="og:image:alt"
                content={facility.primaryMedia.title}
              />
            )}
            {facility.primaryMedia.width && (
              <meta
                property="og:image:width"
                content={"" + facility.primaryMedia.width}
              />
            )}
            {facility.primaryMedia.height && (
              <meta
                property="og:image:height"
                content={"" + facility.primaryMedia.height}
              />
            )}
          </>
        )}
      </Head>

      <main>
        <Container className="page-container" size="xl">
          <Breadcrumbs>
            {facility.park && (
              <Link
                href={`/parks/${facility.park.slug}`}
                legacyBehavior
                passHref>
                <Anchor>{facility.park.name}</Anchor>
              </Link>
            )}
            <Anchor>{title}</Anchor>
          </Breadcrumbs>

          <Space h="md" />

          {!isEmpty(facility.media) && (
            <section className="content-block">
              <MediaCarousel mediaList={facility.media || []} />
            </section>
          )}

          <Grid>
            <Grid.Col span={{base: 12, md: 9}}>
              <section className="content-block">
                <h1>{title}</h1>
                {/* Description */}
                {description && (
                  <Spoiler>
                    <div className="embedded-html">{description}</div>
                  </Spoiler>
                )}
              </section>

              {/* Features */}
              <section className="content-block">
                <div className={classes.features}>
                  <div className={classes.feature}></div>
                </div>
              </section>

              {/* Directions */}
              {facility.directions && (
                <>
                  <Divider />
                  <section className="content-block">
                    <h2>Directions</h2>
                    <Spoiler>{facility.directions}</Spoiler>
                  </section>
                </>
              )}

              {/* Stay limit */}
              {facility.stayLimit && (
                <>
                  <Divider />
                  <section className="content-block">
                    <h2>Stay Limit</h2>
                    <Spoiler>{facility.stayLimit}</Spoiler>
                  </section>
                </>
              )}

              {/* Reviews */}
              <Divider />
              <section className="content-block">
                <h2>Reviews</h2>
                <Group gap="xl" align="flex-start">
                  {facility.rating?.average && (
                    <Rating rating={facility.rating} />
                  )}
                  <Reviews
                    embedded
                    idSelector={facility.id ? {facilityId: facility.id} : null}
                    results={facility.reviews}
                    forType="Facility"
                  />
                </Group>
              </section>
            </Grid.Col>

            <Grid.Col span={{base: 12, md: 3}}>
              <Stack gap="lg">
                <InfoCard title="Availability">
                  <Text size="md">
                    See the <a href="#camp-sites">Camp Sites</a> section below
                    to find detailed booking availability.
                  </Text>
                  <Space h="md" />
                  <Center>
                    <Button.Group>
                      <ReserveFacilityButton
                        size="sm"
                        facility={facility}
                        source="facility_page"
                      />
                      <WatchButton target={facility} size="sm" />
                    </Button.Group>
                  </Center>
                </InfoCard>

                <InfoCard title="Location">
                  <NoSSR>
                    {address &&
                      address.street &&
                      address.city &&
                      address.state && (
                        <div>
                          <a
                            href={
                              (facility.location &&
                                getMapUrl(facility.location)) ||
                              "#"
                            }
                            target="_blank">
                            {address.street}
                            <br />
                            {address.city}, {address.state} {address.zip}
                            <br />
                            {address.country !== "USA" && (
                              <>
                                {address.country}
                                <br />
                              </>
                            )}
                          </a>
                          <br />
                        </div>
                      )}

                    <Text size="md">
                      {facility.phone && (
                        <div>
                          Phone:{" "}
                          <a
                            href={`callto:${encodeURIComponent(facility.phone)}`}>
                            {facility.phone}
                          </a>
                        </div>
                      )}
                      {facility.email && (
                        <div>
                          Email:{" "}
                          <a
                            href={`mailto:${encodeURIComponent(facility.email)}`}>
                            {facility.email}
                          </a>
                        </div>
                      )}
                    </Text>
                  </NoSSR>

                  <Space h="md" />
                  <Center>
                    <Button.Group>
                      {searchMapUrl && (
                        <Button
                          size="sm"
                          variant="default"
                          component="a"
                          href={searchMapUrl}
                          leftSection={<FontAwesomeIcon icon={faMap} />}>
                          Show on map
                        </Button>
                      )}
                    </Button.Group>
                  </Center>
                </InfoCard>
              </Stack>
            </Grid.Col>
          </Grid>

          <Divider />
          <section className="content-block">
            <a id="camp-sites">
              <h2>Camp Sites</h2>
            </a>
            <FacilityUnitsTable
              facility={facility}
              searchInput={searchInput}
              withMap
              withoutFilterEnabled={!withFilterEnabled}
              embedded
            />
          </section>

          {!isEmpty(facility.nearbyFacilities) && (
            <>
              <Divider />
              <section className="content-block">
                <h2>Nearby Campgrounds</h2>
                {searchMapUrl && (
                  <div>
                    <Link href={searchMapUrl}>
                      See all nearby campgrounds on map
                    </Link>
                    <Space h="lg" />
                  </div>
                )}
                <Carousel
                  withIndicators
                  align="start"
                  slideSize="20%"
                  slideGap="xs">
                  {(facility.nearbyFacilities || []).map(
                    ({facility: nearbyFacility, distanceMiles}) => (
                      <Carousel.Slide key={nearbyFacility.id}>
                        <Card
                          className={className(
                            "facility-card",
                            classes.facilityCard
                          )}
                          key={nearbyFacility.slug}
                          withBorder
                          radius="md">
                          <Card.Section
                            className={classes.facilityImageSection}>
                            <Link
                              className="facility-link"
                              href={`/facilities/${nearbyFacility.slug}`}
                              title={nearbyFacility.name || ""}>
                              <Image
                                src={
                                  nearbyFacility.primaryMedia?.url ||
                                  NOT_FOUND_IMAGE_URL
                                }
                                alt="Facility image"
                              />
                            </Link>
                          </Card.Section>
                          <h4 className={classes.facilityTitle}>
                            <Link
                              className="facility-link"
                              href={`/facilities/${nearbyFacility.slug}`}
                              title={nearbyFacility.name || ""}>
                              {nearbyFacility.name}
                            </Link>
                          </h4>
                          <div>
                            <Text size="sm">
                              {distanceMiles.toLocaleString("en-US", {
                                maximumFractionDigits: 1,
                              })}{" "}
                              miles away
                            </Text>
                            {nearbyFacility.unitCount ? (
                              <Text size="sm">
                                {nearbyFacility.unitCount.toLocaleString()} camp{" "}
                                {pluralize("site", nearbyFacility.unitCount)}
                              </Text>
                            ) : (
                              <Text size="sm">No camp sites</Text>
                            )}
                          </div>
                        </Card>
                      </Carousel.Slide>
                    )
                  )}
                </Carousel>
              </section>
            </>
          )}
        </Container>

        <Footer />
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<
  FacilityPageProps
> = async context => {
  let facilityId: number | undefined;
  let facilitySlug: string | undefined;

  const idOrSlug = utils.getParamValue(context.query.slug);
  if (utils.isInt(idOrSlug)) {
    facilityId = parseInt(idOrSlug, 10);
  } else {
    facilitySlug = idOrSlug;
  }

  const queryString = utils.parseQueryString(context.resolvedUrl) || "";
  const searchState = queryStringToSearchState(queryString);
  let withFilterEnabled = true;

  const filtersApplied = areFiltersApplied(searchState.searchInput);
  if (!filtersApplied) {
    const dateFormat = "YYYY-MM-DD";
    const beginDate = dayjs();

    searchState.searchInput ||= {};
    searchState.searchInput.filter ||= {};
    searchState.searchInput.filter.dates = {
      min: beginDate.format(dateFormat),
      max: beginDate.add(DEFAULT_SLOTS_DAYS, "days").format(dateFormat),
    };

    withFilterEnabled = false;
  }

  const {error, data: {entities: {facility = undefined} = {}} = {}} =
    await apolloClient.query({
      query: queries.GET_FACILITY_DETAILS_PAGE,
      variables: {
        id: facilityId,
        slug: facilitySlug,
        filter: searchState.searchInput?.filter,
      },
    });

  if (error || !facility) {
    return {
      notFound: true,
    };
  }

  // Prefer slug over id in URL
  if (facility.slug && (!facilitySlug || facility.slug !== facilitySlug)) {
    let destinationUrl = `/facilities/${facility.slug}`;
    if (queryString) {
      destinationUrl += `?${queryString}`;
    }

    // SECURITY: This allow for potential bot traffic trying to enumerate IDs
    return {
      redirect: {
        destination: destinationUrl,
        permanent: true,
      },
    };
  }

  return {
    props: {
      facility,
      searchInput: searchState?.searchInput,
      withFilterEnabled,
    },
  };
};
